import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  List, ListItem, ListItemText, ListItemSecondaryAction, Switch,
  Typography, Alert, CircularProgress, Box
} from '@mui/material';
import { shareWaxTest, unshareWaxTest, getWaxTestSharedTeams } from '../api';

export default function ShareWaxTestModal({ open, onClose, test }) {
  const [teams, setTeams] = useState([]);
  const [sharedTeams, setSharedTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/teams/my', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setTeams(data);
    } catch (err) {
      setError('Failed to load teams');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSharedTeams = useCallback(async () => {
    if (!test?.id) return;
    try {
      const shared = await getWaxTestSharedTeams(test.id);
      setSharedTeams(shared.map(t => t.id));
    } catch (err) {
      console.error('Failed to load shared teams:', err);
    }
  }, [test?.id]);

  useEffect(() => {
    if (open && test) {
      loadTeams();
      loadSharedTeams();
    }
  }, [open, test, loadTeams, loadSharedTeams]);

  const handleToggleShare = async (teamId, isShared) => {
    try {
      setSaving(true);
      if (isShared) {
        await unshareWaxTest(test.id, teamId);
        setSharedTeams(prev => prev.filter(id => id !== teamId));
      } else {
        await shareWaxTest(test.id, teamId);
        setSharedTeams(prev => [...prev, teamId]);
      }
    } catch (err) {
      setError('Failed to update sharing');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (!test) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Share Wax Test: {test.title}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select which teams can view this wax test. Team members will be able to see the test results and conditions.
            </Typography>
            
            <List>
              {teams.map((team) => {
                const isShared = sharedTeams.includes(team.id);
                return (
                  <ListItem key={team.id}>
                    <ListItemText
                      primary={team.name}
                      secondary={team.description}
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={isShared}
                        onChange={(e) => handleToggleShare(team.id, isShared)}
                        disabled={saving}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>

            {teams.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                You are not a member of any teams yet.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
